import React from 'react';
import { AlertTriangle, AlertOctagon } from 'lucide-react';

/**
 * Inline warning shown directly under the "Paid Amount" input when the
 * entered amount exceeds the requested/billed amount. Purely informational —
 * it does NOT block submission. Designed to be a thin one-liner so it doesn't
 * disturb the drawer/form layout.
 *
 * If `existingPaid` is provided, the component additionally surfaces a
 * stronger warning when the bill is already fully paid and the new entry
 * pushes the cumulative total above the approved amount.
 */
const fmtC = (n) =>
  n == null || Number.isNaN(Number(n))
    ? '$0.00'
    : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const OverpaymentWarning = ({
  paidAmount,
  requestedAmount,
  existingPaid = 0,
  testId = 'overpayment-warning',
}) => {
  const paid = parseFloat(paidAmount || 0) || 0;
  const requested = parseFloat(requestedAmount || 0) || 0;
  const already = parseFloat(existingPaid || 0) || 0;

  // STRONGER WARNING — bill already fully paid AND new entry will push total
  // over the approved/requested amount. This is the "you are about to record
  // an excess payment on a paid bill" scenario.
  if (paid > 0 && requested > 0 && already + 0.01 >= requested
      && already + paid - requested > 0.01) {
    const excess = Math.round((already + paid - requested) * 100) / 100;
    return (
      <div
        className="mt-1.5 flex items-start gap-1.5 p-2 rounded-md bg-rose-50 border border-rose-200 text-[11px] leading-tight text-rose-800"
        data-testid={`${testId}-excess`}
      >
        <AlertOctagon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-rose-600" />
        <span>
          <strong>This bill is already paid</strong> (paid {fmtC(already)} of {fmtC(requested)}). You are
          trying to record an additional <strong>{fmtC(paid)}</strong> — that would push the cumulative paid
          to <strong>{fmtC(already + paid)}</strong> and put the bill <strong>{fmtC(excess)} over</strong> the
          approved amount.
        </span>
      </div>
    );
  }

  // ORIGINAL WARNING — single entry exceeds the requested amount.
  if (!(paid > 0 && requested > 0 && paid - requested > 0.01)) return null;
  const excess = Math.round((paid - requested) * 100) / 100;
  return (
    <p
      className="mt-1 flex items-start gap-1 text-[11px] leading-tight text-rose-700"
      data-testid={testId}
    >
      <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
      <span>
        You are entering more than the requested amount ({fmtC(requested)}). Excess: <strong>{fmtC(excess)}</strong>. Please make sure you are not making a payment more than requested.
      </span>
    </p>
  );
};

export default OverpaymentWarning;
